/**
 * LaunchKit shared types & Zod schemas — Phase 14 Plan 14-09 Task 1.
 *
 * Mirrors the Rust contract published by Plan 14-08
 * (`server/src/services/cms/launch_kits.rs`) verbatim. The backend serialises
 * with `#[serde(rename_all = "camelCase")]`, so no client-side mapping is
 * required — Zod parses the wire shape directly.
 *
 * SendOutcome is a discriminated union (`kind`) — the React console pattern-
 * matches on the discriminator to render Sent/Skipped/Failed badges. The
 * row-level fields (`discordSentAt`, `discordLastError`, etc.) ARE the source
 * of truth post-send; the SendAllResult payload is informational and refreshes
 * the detail query for the row-level fields to redraw the panel.
 *
 * Per `.claude/rules/typescript-standards.md`: Zod schemas are co-located with
 * types; .strict() rejects unknown fields so a backend drift surfaces as a
 * test failure, not a silent UI bug.
 */

import { z } from 'zod'

// ─── Channel content sub-shapes ─────────────────────────────────────────────

/**
 * Discord embed shape (mirrors `services::integrations::discord::DiscordEmbed`).
 * Backend permits extra fields beyond these four, but Plan 14-08 only round-
 * trips this minimal subset through cms_launch_kits.channels JSONB. Use a
 * passthrough .object so future fields don't cause a parse failure for old
 * kits.
 */
export const LaunchKitDiscordEmbedSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
  url: z.string().optional(),
  color: z.number().int().optional(),
})

export const LaunchKitDiscordContentSchema = z.object({
  content: z.string().optional(),
  embeds: z.array(LaunchKitDiscordEmbedSchema).optional(),
})

export const LaunchKitTwitterContentSchema = z.object({
  text: z.string().optional(),
  mediaIds: z.array(z.string()).optional(),
})

export const LaunchKitYouTubeContentSchema = z.object({
  videoId: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  tags: z.array(z.string()).optional(),
  privacyStatus: z.enum(['public', 'unlisted', 'private']).optional(),
})

export const LaunchKitChannelsSchema = z.object({
  discord: LaunchKitDiscordContentSchema.optional(),
  twitter: LaunchKitTwitterContentSchema.optional(),
  youtube: LaunchKitYouTubeContentSchema.optional(),
})

export const LaunchKitMetaSchema = z
  .object({
    newsArticleId: z.string().optional(),
    changelogEntryId: z.string().optional(),
  })
  .passthrough() // server permits extra meta keys; preserve them

// ─── Top-level row ──────────────────────────────────────────────────────────

export const LaunchKitSchema = z
  .object({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    status: z.enum(['draft', 'published']),
    publishedAt: z.string().nullable(),
    channels: LaunchKitChannelsSchema,
    launchStatus: z.enum(['draft', 'ready', 'partial', 'sent']),
    scheduledAt: z.string().nullable(),
    meta: LaunchKitMetaSchema,
    discordApproved: z.boolean(),
    twitterApproved: z.boolean(),
    youtubeApproved: z.boolean(),
    discordSentAt: z.string().nullable(),
    twitterSentAt: z.string().nullable(),
    youtubeSentAt: z.string().nullable(),
    discordLastError: z.string().nullable(),
    twitterLastError: z.string().nullable(),
    youtubeLastError: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict()

export const LaunchKitListResponseSchema = z
  .object({
    items: z.array(LaunchKitSchema),
    total: z.number(),
  })
  .strict()

// ─── Send outcome (discriminated union) ─────────────────────────────────────

export const SendOutcomeSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('sent'),
      sentAt: z.string(),
      response: z.record(z.string(), z.unknown()),
    })
    .strict(),
  z
    .object({
      kind: z.literal('skipped'),
      reason: z.enum([
        'not_approved',
        'empty_content',
        'channel_not_in_kit',
      ]),
    })
    .strict(),
  z
    .object({
      kind: z.literal('failed'),
      error: z.string(),
      code: z.string(),
    })
    .strict(),
])

export const SendAllResultSchema = z
  .object({
    discord: SendOutcomeSchema,
    twitter: SendOutcomeSchema,
    youtube: SendOutcomeSchema,
  })
  .strict()

export const SendStatusSchema = z
  .object({
    discordApproved: z.boolean(),
    twitterApproved: z.boolean(),
    youtubeApproved: z.boolean(),
    discordSentAt: z.string().nullable(),
    twitterSentAt: z.string().nullable(),
    youtubeSentAt: z.string().nullable(),
    discordLastError: z.string().nullable(),
    twitterLastError: z.string().nullable(),
    youtubeLastError: z.string().nullable(),
    launchStatus: z.enum(['draft', 'ready', 'partial', 'sent']),
  })
  .strict()

// ─── Inferred types ─────────────────────────────────────────────────────────

export type LaunchKit = z.infer<typeof LaunchKitSchema>
export type LaunchKitChannels = z.infer<typeof LaunchKitChannelsSchema>
export type LaunchKitMeta = z.infer<typeof LaunchKitMetaSchema>
export type LaunchKitListResponse = z.infer<typeof LaunchKitListResponseSchema>
export type LaunchKitDiscordContent = z.infer<
  typeof LaunchKitDiscordContentSchema
>
export type LaunchKitTwitterContent = z.infer<
  typeof LaunchKitTwitterContentSchema
>
export type LaunchKitYouTubeContent = z.infer<
  typeof LaunchKitYouTubeContentSchema
>
export type LaunchKitDiscordEmbed = z.infer<typeof LaunchKitDiscordEmbedSchema>
export type SendOutcome = z.infer<typeof SendOutcomeSchema>
export type SendAllResult = z.infer<typeof SendAllResultSchema>
export type SendStatus = z.infer<typeof SendStatusSchema>

export type LaunchChannel = 'discord' | 'twitter' | 'youtube'
export const LAUNCH_CHANNELS: readonly LaunchChannel[] = [
  'discord',
  'twitter',
  'youtube',
] as const

// ─── Input shapes (POST/PUT bodies) ─────────────────────────────────────────

/**
 * POST /api/cms/admin/launch-kits — create. `title` is required; the server
 * derives `slug` from title via slugify if omitted (Plan 14-08 service rule).
 */
export interface CreateLaunchKitInput {
  title: string
  slug?: string
  channels?: LaunchKitChannels
  meta?: LaunchKitMeta
  scheduledAt?: string | null
  status?: 'draft' | 'published'
}

/**
 * PUT /api/cms/admin/launch-kits/:id — partial-patch. Each None preserves the
 * existing column; channels REPLACES the whole JSONB on update so passing
 * `{ discord: { content } }` would erase twitter/youtube. Callers must merge
 * pre-existing channels before submitting.
 */
export interface UpdateLaunchKitInput {
  title?: string
  slug?: string
  channels?: LaunchKitChannels
  meta?: LaunchKitMeta
  scheduledAt?: string | null
  status?: 'draft' | 'published'
}

export interface SetChannelApprovalBody {
  approved: boolean
}

export interface SetChannelApprovalResponse {
  approved: boolean
}
